import { ReactNode } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { currencyExact, initials, lineAmount } from './format';
import { LineItem, Sender } from './types';

/**
 * The invoice as the client receives it, laid out like the real tool's: a narrow document column
 * centred in the card. The logo is a monogram of the persona's business name — the demo has no
 * uploaded branding, and a monogram never resembles a real company's mark.
 */

interface InvoicePreviewProps {
    sender: Sender;
    number: string;
    date: string;
    due: string;
    clientName: string;
    clientEmail: string;
    items: LineItem[];
    discountAmount: number;
    total: number;
    message: string;
}

// Description, Qty, Unit Price, Amount. The table is a touch wider than the text above it, as in the real one.
const TABLE_COLUMNS = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 3fr) 1fr 1.2fr 1.2fr',
    gap: 2,
    px: 2,
    py: 1.75,
};

function MetaField({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
    return (
        <Box sx={{ borderTop: 1, borderColor: 'cement.300', pt: 0.75, pb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" component="div">
                {label}
            </Typography>
            {children}
        </Box>
    );
}

interface PreviewRowProps {
    cells: [string, string, string, string];
    total?: boolean;
}

function PreviewRow({ cells, total }: Readonly<PreviewRowProps>) {
    return (
        <Box
            sx={{
                ...TABLE_COLUMNS,
                borderTop: 1,
                borderColor: total ? 'text.secondary' : 'cement.300',
                bgcolor: total ? 'cement.50' : undefined,
            }}>
            {cells.map((cell, i) => (
                <Typography
                    // Columns are positional and fixed, so the index is a stable key.
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    variant={total ? 'captionSemiBold' : 'caption'}
                    align={i === 0 || i === 1 ? 'left' : 'right'}>
                    {cell}
                </Typography>
            ))}
        </Box>
    );
}

export default function InvoicePreview({
    sender,
    number,
    date,
    due,
    clientName,
    clientEmail,
    items,
    discountAmount,
    total,
    message,
}: Readonly<InvoicePreviewProps>) {
    return (
        <Box sx={{ maxWidth: 620, mx: 'auto', py: { md: 2 } }}>
            <Box sx={{ px: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 6 }}>
                    <Avatar variant="medium" color="secondary">
                        {initials(sender.businessName)}
                    </Avatar>
                    <Typography variant="h5" fontWeight={400}>
                        {sender.businessName}
                    </Typography>
                </Stack>

                <Typography variant="h5" fontWeight={400} sx={{ mb: 4 }}>
                    Invoice
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1.1fr 1.3fr' },
                        columnGap: 4,
                        mb: 6,
                    }}>
                    <div>
                        <MetaField label="Invoice number">
                            <Typography variant="caption">{number}</Typography>
                        </MetaField>
                        <MetaField label="Issued date">
                            <Typography variant="caption">{dayjs(date).format('MM/DD/YYYY')}</Typography>
                        </MetaField>
                        <MetaField label="Due date">
                            <Typography variant="caption">{dayjs(due).format('MM/DD/YYYY')}</Typography>
                        </MetaField>
                    </div>
                    <Box sx={{ alignSelf: 'start' }}>
                        <MetaField label="From">
                            <Typography variant="captionSemiBold" component="div">
                                {sender.businessName}
                            </Typography>
                            <Typography variant="caption">{sender.ownerName}</Typography>
                        </MetaField>
                    </Box>
                    <Box sx={{ alignSelf: 'start' }}>
                        <MetaField label="Bill To">
                            <Typography variant="captionSemiBold" component="div">
                                {clientName}
                            </Typography>
                            <Typography variant="caption">{clientEmail}</Typography>
                        </MetaField>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ ...TABLE_COLUMNS, borderTop: 1, borderColor: 'cement.300' }}>
                {['Description', 'Qty', 'Unit Price', 'Amount'].map((heading, i) => (
                    <Typography
                        key={heading}
                        variant="caption"
                        color="text.secondary"
                        align={i === 0 || i === 1 ? 'left' : 'right'}>
                        {heading}
                    </Typography>
                ))}
            </Box>
            {items.map((item) => (
                <PreviewRow
                    key={item.id}
                    cells={[
                        item.description || 'Untitled item',
                        String(item.quantity),
                        currencyExact(item.price),
                        currencyExact(lineAmount(item)),
                    ]}
                />
            ))}
            {discountAmount > 0 && <PreviewRow cells={['Discount', '', '', `-${currencyExact(discountAmount)}`]} />}
            <PreviewRow total cells={['Total due', '', '', currencyExact(total)]} />

            {message && (
                <Box sx={{ mt: 6, px: 2 }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                        Notes
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
